<template>
    <div class="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <!-- Page header -->
        <div class="sm:flex sm:justify-between sm:items-start mb-5">
            <!-- Left: Title and Filters -->
            <div class="space-y-4 mb-4 sm:mb-0">
                <h1 class="text-2xl md:text-3xl text-gray-800 font-bold">
                    Unresolved Accounts
                </h1>
                <div class="flex space-x-4">
                    <button
                        @click="setFilter('open')"
                        :class="[
                            'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                            currentFilter === 'open'
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-500 hover:text-gray-700',
                        ]"
                    >
                        Open Requests ({{ openRequestsCount }})
                    </button>
                    <button
                        @click="setFilter('rejected')"
                        :class="[
                            'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                            currentFilter === 'rejected'
                                ? 'bg-red-50 text-red-700'
                                : 'text-gray-500 hover:text-gray-700',
                        ]"
                    >
                        Rejected Requests ({{ rejectedRequestsCount }})
                    </button>
                </div>
            </div>

            <!-- Right: Search -->
            <div class="relative">
                <input
                    type="text"
                    v-model.trim="searchQuery"
                    placeholder="Search requests..."
                    class="w-64 h-10 pl-10 pr-8 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-800 focus:ring-0 hover:border-slate-800 transition-colors"
                />
                <Icon
                    icon="search"
                    class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none"
                />
                <button
                    v-if="searchQuery"
                    class="block leading-none text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                    @click="clearSearch"
                >
                    <Icon icon="close" />
                </button>
            </div>
        </div>

        <!-- Main Content -->
        <div class="bg-white shadow-lg rounded-sm border border-gray-200 mb-8">
            <header class="px-5 py-4">
                <h2 class="font-semibold text-gray-800">Card Match Requests</h2>
            </header>

            <div class="p-3">
                <div class="overflow-x-auto">
                    <table class="table-auto w-full">
                        <!-- Table header -->
                        <thead
                            class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-t border-b border-gray-200"
                        >
                            <tr>
                                <th
                                    class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                                >
                                    <div
                                        class="font-semibold text-left flex items-center"
                                    >
                                        <span
                                            class="cursor-pointer"
                                            @click="toggleSort('card')"
                                        >
                                            Unresolved Card
                                        </span>
                                        <SortDirection
                                            :direction="sortDirection"
                                            :active-sort="sortField"
                                            column="card"
                                            @sort="
                                                (direction) => {
                                                    sortDirection = direction;
                                                    sortField = 'card';
                                                    loadAccounts();
                                                }
                                            "
                                        />
                                    </div>
                                </th>
                                <th
                                    class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                                >
                                    <div
                                        class="font-semibold text-left flex items-center"
                                    >
                                        <span
                                            class="cursor-pointer"
                                            @click="toggleSort('user')"
                                        >
                                            User
                                        </span>
                                        <SortDirection
                                            :direction="sortDirection"
                                            :active-sort="sortField"
                                            column="user"
                                            @sort="
                                                (direction) => {
                                                    sortDirection = direction;
                                                    sortField = 'user';
                                                    loadAccounts();
                                                }
                                            "
                                        />
                                    </div>
                                </th>
                                <th
                                    class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                                >
                                    <div
                                        class="font-semibold text-left flex items-center"
                                    >
                                        <span
                                            class="cursor-pointer"
                                            @click="toggleSort('submitted')"
                                        >
                                            Submitted
                                        </span>
                                        <SortDirection
                                            :direction="sortDirection"
                                            :active-sort="sortField"
                                            column="submitted"
                                            @sort="
                                                (direction) => {
                                                    sortDirection = direction;
                                                    sortField = 'submitted';
                                                    loadAccounts();
                                                }
                                            "
                                        />
                                    </div>
                                </th>
                                <th
                                    class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                                >
                                    <div class="font-semibold text-left">
                                        Actions
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <!-- Table body -->
                        <tbody class="text-sm divide-y divide-gray-200">
                            <tr v-if="accounts.data.length === 0">
                                <td
                                    colspan="4"
                                    class="px-2 first:pl-5 last:pr-5 py-3 text-center"
                                >
                                    No unresolved accounts found
                                </td>
                            </tr>
                            <tr
                                v-for="account in accounts.data"
                                :key="account.id"
                            >
                                <td
                                    class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                                >
                                    <div class="font-medium text-gray-800">
                                        {{ account.card_not_found_name }}
                                    </div>
                                    <div class="text-sm text-gray-500">
                                        Issuing Bank:
                                        {{ account.issuing_bank || 'Unknown' }}
                                    </div>
                                </td>
                                <td
                                    class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                                >
                                    <div class="text-left">
                                        {{
                                            account.user?.email ||
                                            'Unknown User'
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
                                >
                                    <div class="text-left">
                                        {{ formatDate(account.created_at) }}
                                    </div>
                                </td>
                                <td class="px-2 first:pl-5 last:pr-5 py-3">
                                    <div class="space-y-3">
                                        <!-- Resolve Section -->
                                        <div class="bg-gray-50 rounded-lg p-3">
                                            <h3
                                                class="text-sm font-medium text-gray-700 mb-2"
                                            >
                                                Match to Existing Card
                                            </h3>
                                            <CardSearch
                                                v-model="account.selectedCard"
                                                class="mb-2"
                                            />
                                            <button
                                                @click="resolveAccount(account)"
                                                :disabled="
                                                    !account.selectedCard
                                                "
                                                class="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Resolve Match
                                            </button>
                                        </div>

                                        <!-- Reject Section -->
                                        <div class="bg-gray-50 rounded-lg p-3">
                                            <h3
                                                class="text-sm font-medium text-gray-700 mb-2"
                                            >
                                                Reject Request
                                            </h3>
                                            <textarea
                                                v-model="account.rejectedReason"
                                                class="form-textarea w-full mb-2 text-sm"
                                                placeholder="Reason for rejection..."
                                                rows="2"
                                            ></textarea>
                                            <button
                                                @click="rejectAccount(account)"
                                                :disabled="
                                                    !account.rejectedReason
                                                "
                                                class="btn btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Reject Match
                                            </button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div class="mt-6">
            <Pagination
                v-if="accounts.meta && accounts.meta.total > 0"
                :total-pages="accounts.meta.last_page"
                :total-results="accounts.meta.total"
                :current-page="currentPage"
                :per-page="accounts.meta.per_page"
                @pageChanged="handlePageChange"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import { notify } from 'notiwind';
import Icon from '@/components/ui/icon/icon.vue';
import CardSearch from '@/components/ui/card-search.vue';
import SortDirection from '@/components/ui/sort-direction/sort-direction.vue';
import Pagination from '@/components/ui/pagination/pagination.vue';
import filters from '@/filters';
import dayjs from 'dayjs';

const searchQuery = ref('');
const accounts = ref({ data: [], meta: null });
const currentPage = ref(1);
const sortField = ref('submitted');
const sortDirection = ref('desc');
const currentFilter = ref('open');
const openRequestsCount = ref(0);
const rejectedRequestsCount = ref(0);

const formatDate = (date) => {
    // Convert UTC to local timezone and format
    return (
        filters.formatDate(date, 'UTC', dayjs.tz.guess()) +
        ' ' +
        dayjs.utc(date).local().format('h:mm:ss A')
    );
};

const loadAccounts = async () => {
    try {
        const params = {
            sort: sortField.value,
            direction: sortDirection.value,
            page: currentPage.value,
            search: searchQuery.value || undefined,
            filter: currentFilter.value,
        };

        console.log('Loading accounts with params:', params);

        const response = await axios.get('/api/admin/unresolved-accounts', {
            params,
        });
        console.log('API Response:', response.data);

        accounts.value = {
            data: response.data.data,
            meta: {
                current_page: response.data.current_page,
                last_page: response.data.last_page,
                per_page: response.data.per_page,
                total: response.data.total,
            },
        };

        // Update counts
        openRequestsCount.value = response.data.counts.open;
        rejectedRequestsCount.value = response.data.counts.rejected;
    } catch (error) {
        console.error('Error loading unresolved accounts:', error);
    }
};

const handlePageChange = (page) => {
    currentPage.value = page;
    loadAccounts();
};

const clearSearch = () => {
    searchQuery.value = '';
    currentPage.value = 1;
    loadAccounts();
};

// Watch for search changes and update immediately
watch(searchQuery, () => {
    currentPage.value = 1; // Reset to first page when searching
    loadAccounts();
});

// Watch for sort changes
watch([sortField, sortDirection], () => {
    currentPage.value = 1; // Reset to first page when sorting
    loadAccounts();
});

const toggleSort = (field) => {
    if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortField.value = field;
        sortDirection.value = 'desc';
    }
    loadAccounts();
};

const setFilter = (filter) => {
    currentFilter.value = filter;
    currentPage.value = 1; // Reset to first page when changing filter
    loadAccounts();
};

// Watch for filter changes
watch(currentFilter, () => {
    currentPage.value = 1; // Reset to first page when filter changes
    loadAccounts();
});

const rejectAccount = async (account) => {
    if (!account.rejectedReason) {
        notify({
            type: 'error',
            group: 'error',
            title: 'Error',
            text: 'Please provide a reason for rejection',
        });
        return;
    }

    try {
        await axios.put(`/admin/reject-card-matches/${account.id}`, {
            rejected_reason: account.rejectedReason,
            rejected: true,
        });
        notify({
            type: 'success',
            group: 'success',
            title: 'Success',
            text: 'Card match request rejected successfully',
        });
        await loadAccounts();
    } catch (error) {
        notify({
            type: 'error',
            group: 'error',
            title: 'Error',
            text: 'Error rejecting card match: ' + error.message,
        });
    }
};

const resolveAccount = async (account) => {
    if (!account.selectedCard) {
        notify({
            type: 'error',
            group: 'error',
            title: 'Error',
            text: 'Please select a card to match',
        });
        return;
    }

    try {
        await axios.put(`/api/admin/resolve-account/${account.id}`, {
            card_id: account.selectedCard.id,
            resolved: true,
        });
        notify({
            type: 'success',
            group: 'success',
            title: 'Success',
            text: 'Account successfully matched',
        });
        await loadAccounts();
    } catch (error) {
        notify({
            type: 'error',
            group: 'error',
            title: 'Error',
            text: 'Error resolving account: ' + error.message,
        });
    }
};

onMounted(() => {
    loadAccounts();
});
</script>
